import { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Chip, Switch, FormControlLabel, Button } from '@mui/material';
import PortalAutocomplete from './PortalAutocomplete';

const apiPrefix = window.location.hostname === 'localhost' ? '' : '/api';

/**
 * Shared portal picker — used in AlertCreate, AlertDetails, ScheduledSearchForm.
 *
 * Props:
 *   value    : [{id: number, name: string}]  — controlled selection
 *   onChange : (newPortals) => void
 */
export default function PortalPicker({ value = [], onChange, showChips = true, autoDetectForeign = false }) {
    const [domesticOptions, setDomesticOptions] = useState([]);
    const [foreignOptions, setForeignOptions] = useState([]);
    const [showForeignPicker, setShowForeignPicker] = useState(false);

    useEffect(() => {
        axios.get(`${apiPrefix}/getDomesticPortals`, { withCredentials: true })
            .then(r => {
                const data = Array.isArray(r.data) ? r.data : [];
                setDomesticOptions(data.map(p => ({ id: p.id, name: p.name ?? p.portal_name ?? '' })).sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => setDomesticOptions([]));
    }, []);

    useEffect(() => {
        if ((!showForeignPicker && !autoDetectForeign) || foreignOptions.length > 0) return;
        axios.get(`${apiPrefix}/getForeignPortals`, { withCredentials: true })
            .then(r => {
                const data = Array.isArray(r.data) ? r.data : [];
                setForeignOptions(data.map(p => ({ id: p.id, name: p.name ?? p.portal_name ?? '' })).sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => setForeignOptions([]));
    }, [showForeignPicker, autoDetectForeign]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-show foreign picker when editing items that already have foreign portals
    useEffect(() => {
        if (!autoDetectForeign || foreignOptions.length === 0 || value.length === 0) return;
        const fIds = new Set(foreignOptions.map(o => o.id));
        if (value.some(p => fIds.has(p.id))) setShowForeignPicker(true);
    }, [autoDetectForeign, foreignOptions, value]);

    const domesticIds = new Set(domesticOptions.map(o => o.id));
    const foreignIds  = new Set(foreignOptions.map(o => o.id));

    const handleDomesticChange = (_, selected) => {
        const rest = value.filter(p => !domesticIds.has(p.id));
        onChange([...rest, ...selected]);
    };

    const handleForeignChange = (_, selected) => {
        const rest = value.filter(p => !foreignIds.has(p.id));
        onChange([...rest, ...selected]);
    };

    const handleAddAllDomestic = () => {
        const rest = value.filter(p => !domesticIds.has(p.id));
        onChange([...rest, ...domesticOptions]);
    };

    const handleAddAllForeign = () => {
        const rest = value.filter(p => !foreignIds.has(p.id));
        onChange([...rest, ...foreignOptions]);
    };

    return (
        <Box>
            {/* Selected portals as chips — hidden when showChips=false */}
            {showChips && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5, minHeight: 32 }}>
                    {value.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Nema odabranih portala</Typography>
                    ) : (
                        [...value].sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                            <Chip
                                key={p.id}
                                label={p.name}
                                onDelete={() => onChange(value.filter(x => x.id !== p.id))}
                                size="small"
                                color="primary"
                            />
                        ))
                    )}
                </Box>
            )}

            {/* Domestic portals */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Domaći portali</Typography>
                <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={handleAddAllDomestic}
                    disabled={domesticOptions.length === 0}
                    sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                >
                    Dodaj sve
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disableElevation
                    onClick={() => onChange(value.filter(p => !domesticIds.has(p.id)))}
                    disabled={!value.some(p => domesticIds.has(p.id))}
                    sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                >
                    Ukloni sve
                </Button>
            </Box>
            <PortalAutocomplete
                options={domesticOptions}
                value={value.filter(p => domesticIds.has(p.id))}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                onChange={handleDomesticChange}
                sx={{ mb: 2 }}
            />

            {/* Foreign portals — lazy, behind a switch */}
            <FormControlLabel
                control={
                    <Switch
                        checked={showForeignPicker}
                        onChange={e => setShowForeignPicker(e.target.checked)}
                    />
                }
                label="Strani portali"
            />
            {showForeignPicker && (
                <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Strani portali</Typography>
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onClick={handleAddAllForeign}
                            disabled={foreignOptions.length === 0}
                            sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                        >
                            Dodaj sve
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            disableElevation
                            onClick={() => onChange(value.filter(p => !foreignIds.has(p.id)))}
                            disabled={!value.some(p => foreignIds.has(p.id))}
                            sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', py: 0.5, px: 1.5 }}
                        >
                            Ukloni sve
                        </Button>
                    </Box>
                    <PortalAutocomplete
                        options={foreignOptions}
                        value={value.filter(p => foreignIds.has(p.id))}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        onChange={handleForeignChange}
                    />
                </Box>
            )}
        </Box>
    );
}
